import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/agents/metodej/learnings`: what Hubert found in people's prompts for Metoděj.
 * Only Active lessons reach Metoděj's prompt; every decision can be undone.
 */

type Status = "draft" | "active" | "dismissed";

type Learning = {
  id: string;
  text: string;
  evidence: string;
  evidenceCount: number;
  status: Status;
  /** Why Hub dismissed it on its own; null when a person did. */
  resolvedNote: string | null;
  createdAt: string;
  project: { id: string; name: string } | null;
  sourceUser: { id: string; name: string | null } | null;
  reviewedBy: { id: string; name: string | null } | null;
};

const TABS = [
  { key: "draft", label: "Draft" },
  { key: "active", label: "Active" },
  { key: "dismissed", label: "Dismissed" },
  { key: "all", label: "All" },
] as const;
type Tab = (typeof TABS)[number]["key"];

export function CoderLearningsPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [tab, setTab] = useState<Tab>("draft");
  const [rows, setRows] = useState<Learning[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [mineMessage, setMineMessage] = useState<string | null>(null);

  const load = useCallback((t: Tab) => {
    setRows(null);
    return api
      .get<{ learnings: Learning[] }>(`/api/coder-learnings${t === "all" ? "" : `?status=${t}`}`)
      .then((res) => setRows(res.learnings))
      .catch(() => setError("Couldn't load learnings."));
  }, []);

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    void load(tab);
  }, [user.globalRole, tab, load]);

  async function save(id: string, body: { status?: Status; text?: string }) {
    setBusyId(id);
    setError(null);
    try {
      await api.patch(`/api/coder-learnings/${id}`, body);
      setEditing(({ [id]: _, ...rest }) => rest);
      await load(tab);
    } catch {
      setError("Couldn't save that.");
    } finally {
      setBusyId(null);
    }
  }

  async function mineNow() {
    setMineMessage(null);
    try {
      await api.post("/api/coder-learnings/mine", {});
      setMineMessage("Mining started, check back in a few minutes.");
    } catch (err) {
      setMineMessage(
        err instanceof ApiError && err.status === 503
          ? "AI isn't configured."
          : "Couldn't start mining.",
      );
    }
  }

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Metoděj's Learnings</h2>
          <p className="text-sm text-muted-foreground">
            Lessons Hubert found in people's own prompts to Claude Code: conventions of a project,
            and how someone wants work done in general. Only Active ones reach Metoděj, and taking
            one back removes it from his next ticket.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button type="button" variant="outline" size="sm" onClick={() => void mineNow()}>
            Mine now
          </Button>
          {mineMessage && <p className="text-xs text-muted-foreground">{mineMessage}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`coder-learnings-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="coder-learnings-empty">
          Nothing here.
        </p>
      )}

      {rows?.map((r) => {
        const draft = editing[r.id];
        return (
          <Card key={r.id} data-testid="coder-learning-card">
            <CardContent className="space-y-2 pt-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary">{r.project ? r.project.name : "General style"}</Badge>
                <span>
                  seen {r.evidenceCount}× · from {r.sourceUser?.name ?? "someone"}'s prompts
                </span>
                {r.reviewedBy && r.status !== "draft" && (
                  <span>
                    · {r.status === "active" ? "activated" : "dismissed"} by {r.reviewedBy.name}
                  </span>
                )}
              </div>
              {r.status === "dismissed" && r.resolvedNote && (
                <p className="text-xs text-muted-foreground" data-testid="learning-resolved-note">
                  Dismissed by Hub: {r.resolvedNote}
                </p>
              )}
              {draft === undefined ? (
                <p className="text-sm">{r.text}</p>
              ) : (
                <textarea
                  className="w-full rounded-md border bg-background p-2 text-sm"
                  rows={3}
                  value={draft}
                  aria-label="Learning text"
                  onChange={(e) => setEditing((prev) => ({ ...prev, [r.id]: e.target.value }))}
                />
              )}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Why</summary>
                <p className="mt-1 whitespace-pre-wrap">{r.evidence}</p>
              </details>
              <div className="flex flex-wrap gap-2">
                {draft !== undefined ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === r.id || !draft.trim()}
                      onClick={() => void save(r.id, { text: draft })}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditing(({ [r.id]: _, ...rest }) => rest)}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing((prev) => ({ ...prev, [r.id]: r.text }))}
                  >
                    Edit
                  </Button>
                )}
                {r.status !== "active" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "active" })}
                  >
                    Activate
                  </Button>
                )}
                {r.status !== "dismissed" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "dismissed" })}
                  >
                    Dismiss
                  </Button>
                )}
                {r.status !== "draft" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void save(r.id, { status: "draft" })}
                  >
                    Back to draft
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
